import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import {
  ContentContainerComponent,
  EmptyStateComponent,
  OrganisationService,
  PipesModule as PrinciplePipesModule,
} from '@principle-theorem/ng-principle-shared';
import {
  extendSortingDataAccessor,
  ObservableDataSource,
  timestampSortingAccessor,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { Referral, stafferToNamedDoc } from '@principle-theorem/principle-core';
import {
  IReferral,
  IStaffer,
  MentionResourceType,
  ReferralActor,
  ReferralType,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  HISTORY_DATE_FORMAT,
  INamedDocument,
  toNamedTypeDocument,
  WithRef,
} from '@principle-theorem/shared';
import { MomentModule } from 'ngx-moment';
import { map } from 'rxjs/operators';
import { ReferralListStore } from '../../stores/referral-list.store';
import { ReferralMenuStatusButtonsComponent } from '../referral-menu-status-buttons/referral-menu-status-buttons.component';
import { ReferralStatusComponent } from '../referral-status/referral-status.component';
import { ReferralTypeChipComponent } from '../referral-type-chip/referral-type-chip.component';

@Component({
  selector: 'pr-referral-list',
  imports: [
    NgCommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltipModule,
    EmptyStateComponent,
    PrinciplePipesModule,
    RouterLink,
    MomentModule,
    MatButtonModule,
    MatProgressBarModule,
    MatIconModule,
    MatMenuModule,
    ContentContainerComponent,
    ReferralTypeChipComponent,
    ReferralStatusComponent,
    ReferralMenuStatusButtonsComponent,
    MatDividerModule,
  ],
  templateUrl: './referral-list.component.html',
  styleUrl: './referral-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralListComponent implements AfterViewInit, OnDestroy {
  private _organisation = inject(OrganisationService);
  public store = inject(ReferralListStore);
  trackByRef = TrackByFunctions.ref<WithRef<IReferral>>();

  referralSelected = output<WithRef<IReferral>>();
  referralRemoved = output<WithRef<IReferral>>();
  referralUpdated = output<{
    referral: WithRef<IReferral>;
    changes: Partial<WithRef<IReferral>>;
  }>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  emptySearch = signal(false);
  currentStaffer = signal<INamedDocument<IStaffer> | undefined>(undefined);
  totalRecordCount = toSignal(
    this.store.filteredReferrals$.pipe(map((col) => col.length))
  );

  table: ObservableDataSource<WithRef<IReferral>>;
  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly referralType = ReferralType;
  readonly pageSizeOptions = [50, 100, 200];
  readonly pageSize = 50;
  readonly displayedColumns = [
    'patient',
    'type',
    'status',
    'createdAt',
    'urgency',
    'referrer',
    'recipient',
    'actions',
  ];

  constructor() {
    this._organisation.staffer$
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((staffer) =>
        this.currentStaffer.set(stafferToNamedDoc(staffer))
      );

    this.table = new ObservableDataSource(this.store.filteredReferrals$);

    this.table.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );

    this.table.filterPredicate = (data, filter) =>
      this._filterPredicate(data, filter);
  }

  ngAfterViewInit(): void {
    this.table.paginator = this.paginator;
    this.table.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.table.disconnect();
  }

  hasUnreadChanges(
    args: [INamedDocument<IStaffer> | undefined, WithRef<IReferral>]
  ): boolean {
    const [staffer, record] = args;
    if (!staffer || !record) {
      return false;
    }
    try {
      const actor: ReferralActor = toNamedTypeDocument(
        staffer,
        MentionResourceType.Staffer
      );
      return Referral.hasUnreadChanges(record, actor);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking unread changes:', error);
      return false;
    }
  }

  handleMenuAction(
    event: Event,
    action: string,
    record: WithRef<IReferral>
  ): void {
    event.stopPropagation();
    switch (action) {
      case 'edit':
        this.referralSelected.emit(record);
        break;
      case 'delete':
        this.referralRemoved.emit(record);
        break;
      default:
        return;
    }
  }

  private _filterPredicate(data: WithRef<IReferral>, filter: string): boolean {
    const searchStr = filter.trim().toLowerCase();
    const filterValues = [
      data.patient?.name,
      data.referrer?.name,
      data.recipient?.name,
      data.type,
      data.status,
    ];
    return filterValues.some(
      (value) => value?.toLowerCase().includes(searchStr)
    );
  }

  private _sortingDataAccessor(
    data: WithRef<IReferral>,
    sortHeaderId: string
  ): string | undefined {
    switch (sortHeaderId) {
      case 'patient':
        return data.patient?.name?.toLowerCase();
      case 'type':
        return data.type;
      case 'status':
        return data.status;
      case 'urgency':
        return data.urgency;
      case 'referrer':
        return data.referrer?.name?.toLowerCase();
      case 'recipient':
        return data.recipient?.name?.toLowerCase();
      case 'createdAt':
        return timestampSortingAccessor(data.createdAt);
      default:
        return;
    }
  }
}
